import { Injectable, signal } from "@angular/core";
import { Measurement, MeasurementStateService } from "@evident/ngx-media-viewer";
import { MetadataService } from "@evident/ngx-metadata-writer";
import { NetworkStatusService } from "../../../../_services/network-status.service";
import { ElectronService } from "../../../../electron.service";
import { IPC_CHANNEL } from "../../../../_offline/electron.constants";
import { firstValueFrom } from "rxjs";
import { InspectionImage } from "../../../../_objects/inspection-image";
import { AnnotationObjectForUpdate } from "../../../../_objects/inspection-image-annotation";
import { AnnotationImageService } from "../../../../_services/http-api/annotation-image.service";
import { InspectionImageService } from "../../../../_services/http-api/inspection-image.service";
import { TenantConfigService } from "../../../../_services/http-api/tenant-config.service";
import { FileSystemAccessService } from "../../../../_services/file-system-access.service";
import { MerryMeasurementItem, MerryMeasurementItems } from "../../../../_services/merry-measurement-items";
import { MerryMeasurementParserService } from "../../../../_services/merry-measurement-parser.service";
import { TenantConfig } from "../../../../app.constants";
import { isMetric, UnitSystem, unitSystemOfUnitLabel } from "../measurement.type";

export const metadataMeasurementTag = "Measurements";
export const XMPMeasurementTag = "XMP-xmp:" + metadataMeasurementTag;

@Injectable({ providedIn: "root" })
export class MeasurementService {
  /** The unit the results are listed in and saved with; see initializeUnitSystem. */
  public readonly unitSystem = signal<UnitSystem>("Metric");

  constructor(
    private measurementState: MeasurementStateService,
    private _metadataService: MetadataService,
    private _annotationService: AnnotationImageService,
    private networkService: NetworkStatusService,
    private electronService: ElectronService,
    private inspectionImageService: InspectionImageService,
    private fileSystemAccessService: FileSystemAccessService,
    private measurementParser: MerryMeasurementParserService,
    private tenantConfigService: TenantConfigService
  ) {}

  /**
   * Starts the measurement session of an image: its results show in the unit they were last saved with (this tab and
   * the legacy one both store it on the image's measurement items), else in the tenant's default unit.
   */
  public initializeUnitSystem(measurementItems: unknown): void {
    const stored = MerryMeasurementItems.unitSystemOf(measurementItems);
    this.unitSystem.set(stored ?? unitSystemOfUnitLabel(this.tenantConfigService.getConfigBykey(TenantConfig.DefaultUnitOfMeasurementResult.KEY)));
  }

  public toggleUnitSystem(): void {
    this.unitSystem.update((unitSystem) => (isMetric(unitSystem) ? "Imperial" : "Metric"));
  }

  public async retrieveExistingMeasurements(imageSrc: string): Promise<void> {
    const measurements = await this.measurementParser.parseMeasurementResults(imageSrc);
    if (measurements.length) this.measurementState.setMeasurements(measurements);
  }

  /**
   * Keeps the measurement being drawn when it is complete and drops it otherwise, so a save or a tool change
   * never loses a finished reading nor stores a partial one.
   */
  public async completeInProgressMeasurement(): Promise<void> {
    const inProgress = this.measurementState.temporaryMeasurement;
    if (!inProgress) return;

    if (inProgress.isValid()) {
      this.measurementState.addMeasurement(inProgress);
    } else {
      await this.measurementState.resetTemporaryMeasurements();
    }
  }

  public async saveMeasurements(imageSrc: string, inspectionImage: InspectionImage, base64ImagePreview: string) {
    const measurements = this.measurementState.measurements;
    await this.writeMetadata(imageSrc, measurements);
    await this.saveMeasuredImage(
      inspectionImage,
      base64ImagePreview,
      imageSrc,
      MerryMeasurementItems.toMeasurementItems(measurements, this.unitSystem())
    );
  }

  private async writeMetadata(imageSrc: string, measurements: Measurement[]) {
    const metadata: Record<string, string> = {};

    // Stored in the media viewer's own metadata format (`version: 1`), the one Merry writes and reads.
    const storedMeasurements = measurements.map((measurement) => measurement.toMetadata());
    metadata[XMPMeasurementTag] = await this._metadataService.encryptMetadata(JSON.stringify(storedMeasurements));
    await this._metadataService.embedMetadata(imageSrc, metadata);
  }

  private async saveMeasuredImage(
    inspectionImage: InspectionImage,
    base64ImagePreview: string,
    imageSrc: string,
    measurementItems: MerryMeasurementItem[]
  ) {
    // The items must be a list (null means "no measured image" to the server). They carry the values and the chosen
    // unit in the legacy shape, so the web report, the desktop report and the sync show them like the legacy tab's.
    const annotationReq = new AnnotationObjectForUpdate(inspectionImage.id, null, measurementItems, inspectionImage.schemaVersion);
    const measurementPreviewFile = await this.base64ToFile(base64ImagePreview, inspectionImage.fileName);

    await firstValueFrom(this._annotationService.putImageAnnotations(annotationReq, inspectionImage.inspectionId, 2));
    await firstValueFrom(this._annotationService.putModifiedImage(inspectionImage.inspectionId, inspectionImage.id, measurementPreviewFile, 2));

    // Online this uploads the original (with the measurement metadata in its XMP) right away;
    // offline and on-premise it registers the rewritten original for the next push sync.
    const localPath = decodeURIComponent(imageSrc.replace("file:///", ""));
    const originalFileWithMetadata = await this.fileSystemAccessService.getFile(localPath, inspectionImage.fileName, inspectionImage.mimeType, true);
    await firstValueFrom(
      this.inspectionImageService.updateInspectionImageFile(inspectionImage.inspectionId, inspectionImage.id, originalFileWithMetadata)
    );
  }

  private async base64ToFile(base64: string, fileName: string) {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type });
  }

  public async getLocalImageUrl(imageSrc: string, inspectionImage: InspectionImage): Promise<string> {
    if (this.networkService.isOffline) {
      return imageSrc;
    } else {
      return await this.electronService.invoke(IPC_CHANNEL.FILE.SAVE_FILE, inspectionImage.originalFilePath, inspectionImage.fileName);
    }
  }
}
